import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'

import {
  BenefitParticipationDecision,
  MakeOptInOptOutDecisionRequest,
  ResendRenewalNoticeRequest,
  SyncWithAdpProps,
  getRenewalPlanPeriod,
  getUsersFromExistingProduct,
  makeOptInOptOutDecision,
  resendRenewalNotice,
  syncWithAdp,
} from 'core'
import { TaskStatus } from 'core/application/adminDashboard/adminTasks'
import {
  GetSeatCountStatisticsResponse,
  getSeatCountStatistics,
} from 'core/application/adminDashboard/managePeople/seats/getSeatCountStatistics'
import { UserToEditLogin } from 'core/application/adminDashboard/managePeople/viewPeople/editLoginUsername'
import { UserToTerminate } from 'core/application/adminDashboard/managePeople/viewPeople/terminateEmployee'
import { EmployeeResponse } from 'core/application/companies/getEmployees'
import getCompanyProductTaskDetails, {
  CompanyProductTaskDetailsResponse,
} from 'core/application/tasks/getCompanyProductTaskDetails'
import ProductTaskCommonIds from 'core/application/tasks/productTaskCommonIds'

import useActivePlanPeriod from 'common/hooks/useActivePlanPeriod'

import { Button, Col, Drawer, Icon, Notification, Row, Skeleton } from 'common/components'
import { useCommandRequestV2, useLoginUserV2, useQueryRequestV2 } from 'common/hooks'

import Input from '../../../../common/components/dataEntry/components/Input'
import useUrlQueryParam from '../../../../common/hooks/useUrlQueryParam'
import { ROUTE_PATHS } from '../../../../common/routePaths'
import AddPeopleForm from '../addPeopleForm'
import CompleteAdministratorProfile from '../completeAdministratorProfileAlert'
import EditLoginUsernameModal from '../editLoginUsername/EditLoginUsernameModal'
import OptInOptOutModal from '../optInOptOutModal'
import TerminateUserModal from '../termination/TerminateUserModal'
import EditSeatModal from './editSeatModal'
import EmployeesTable from './employeesTable'
import InEligibleEmployeesTable from './inEligibleEmployeesTable'
import SeatStatistic from './seatStatistic'
import TerminatedEmployeesTable from './terminatedEmployeesTable'

const ViewPeople = () => {
  const history = useHistory()
  const { t: translate } = useTranslation()

  const [userToOptIn, setUserToOptIn] = useState<MakeOptInOptOutDecisionRequest | null>(null)
  const [userToOptOut, setUserToOptOut] = useState<MakeOptInOptOutDecisionRequest | null>(null)
  const [userToOptInBenefitEligibilityDate, setUserToOptInBenefitEligibilityDate] = useState<Date>(null)
  const [userFirstName, setUserFirstName] = useState<string>(null)
  const [reloadEligibleEmployeeTable, setReloadEligibleEmployeeTable] = useState<boolean>(false)
  const [reloadInEligibleEmployeeTable, setReloadInEligibleEmployeeTable] = useState<boolean>(false)
  const [reloadTerminatedEmployeeTable, setReloadTerminatedEmployeeTable] = useState<boolean>(false)
  const [editSeatModalVisible, setEditSeatModalVisible] = useState<boolean>(false)
  const [showAddEmployeeDrawer, setShowAddEmployeeDrawer] = useState<boolean>(false)
  const [terminatedButton, setTerminatedButton] = useState<boolean>(false)
  const [search, setSearch] = useState<string | null | undefined>(undefined)
  const [userToTerminate, setUserToTerminate] = useState<UserToTerminate | null>(null)
  const [userToEditLogin, setUserToEditLogin] = useState<UserToEditLogin | null>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const paramValue = useUrlQueryParam('fromtask')
  const [fromTask] = useState<boolean>(!!paramValue)

  const [planPeriod] = useActivePlanPeriod()
  const { user } = useLoginUserV2()

  const {
    data: seatsStats,
    isLoading: seatsStatsLoading,
    sendQuery: sendGetSeatStatisticRequest,
  } = useQueryRequestV2<GetSeatCountStatisticsResponse | null>()

  const { data: productTaskDetails } = useQueryRequestV2<CompanyProductTaskDetailsResponse>({
    initialState: null,
    queryFn: () => getCompanyProductTaskDetails(ProductTaskCommonIds.CompleteProfile),
  })

  const { data: employeesInfo } = useQueryRequestV2({
    initialState: null,
    queryFn: () => getUsersFromExistingProduct(),
  })

  const { data: renewalPlanPeriod } = useQueryRequestV2({ initialState: null, queryFn: () => getRenewalPlanPeriod() })

  const { sendCommand: sendMakeDecision, isLoading: isMakeDecisionLoading } = useCommandRequestV2(
    (request: MakeOptInOptOutDecisionRequest) => makeOptInOptOutDecision(request),
  )

  const { sendCommand: sendResendNotice, isLoading: isResendNoticeLoading } = useCommandRequestV2(
    (request: ResendRenewalNoticeRequest) => resendRenewalNotice(request),
  )

  const handleOptIn = async () => {
    setUserToOptIn(null)
    setUserToOptInBenefitEligibilityDate(null)
    setUserFirstName(null)
    Notification.open({
      message: `Employee opted in successfully`,
      type: 'success',
    })
    setReloadEligibleEmployeeTable(true)
    setReloadInEligibleEmployeeTable(true)
    setReloadTerminatedEmployeeTable(true)
    await reloadStats()
  }

  const handleOptOut = async () => {
    setUserToOptOut(null)
    setUserFirstName(null)
    Notification.open({
      message: `Employee opted out successfully`,
      type: 'success',
    })
    setReloadEligibleEmployeeTable(true)
    setReloadInEligibleEmployeeTable(true)
    setReloadTerminatedEmployeeTable(true)
    await reloadStats()
  }

  const reloadStats = async () => {
    await sendGetSeatStatisticRequest(() => getSeatCountStatistics(planPeriod.id))
  }

  const toggleEditSeatModal = useCallback(() => {
    setEditSeatModalVisible(!editSeatModalVisible)
  }, [editSeatModalVisible])

  const onAddPeopleClicked = async () => {
    setShowAddEmployeeDrawer(true)
  }

  const { sendCommand: sendSyncWithAdp } = useCommandRequestV2((request: SyncWithAdpProps) => syncWithAdp(request))

  const onSyncPeopleClicked = async () => {
    const result = await sendSyncWithAdp({
      companyId: user.companyId,
      productId: user.productId,
    })
    if (result.completed) {
      Notification.success('Sync with ADP scheduled successfully')
    }
  }

  const onClickTerminatedButton = async () => {
    setTerminatedButton(!terminatedButton)
  }

  useEffect(() => {
    if (fromTask) {
      setShowAddEmployeeDrawer(true)
    }
  }, [fromTask])

  const onSearch = (e: any) => {
    if (e.keyCode == 13) {
      setSearch(e.target.value)
    }
  }

  useEffect(() => {
    ;(async () => {
      if (planPeriod) {
        await reloadStats()
      }
    })()
  }, [planPeriod])

  const onEmployeeSaved = async (addMore: boolean) => {
    if (!addMore && fromTask) {
      history.push(ROUTE_PATHS.DASHBOARD_PATH)
      return
    }

    if (!addMore) {
      setShowAddEmployeeDrawer(false)
    }

    await reloadStats()
    setReloadEligibleEmployeeTable(true)
    setReloadInEligibleEmployeeTable(true)
  }

  const handleGetOtherProductName = () => {
    const otherProduct = user.products.filter((product) => product.id !== user.productId)[0]
    return otherProduct.name
  }

  return (
    <Skeleton loading={!planPeriod}>
      {planPeriod && (
        <div className={'qsehra-view-people-wrapper'} data-testid={'manage-people'}>
          <Drawer
            title={
              <Row className="perks-add-people-drawer__header">
                <Col>{translate('admin-dashboard.manage-people.add-people.title')}</Col>

                <Col className="perks-add-people-drawer__seats">
                  <Row>
                    <Col className="perks-add-people-drawer__seats-label center-align-items mr-8">
                      {translate('admin-dashboard.manage-people.add-people.filling-seats-label')}{' '}
                      {seatsStats && (
                        <span>
                          {' '}
                          {seatsStats.occupiedSeats}/{seatsStats.totalSeatCount}
                        </span>
                      )}
                    </Col>
                    <Col>
                      <Button
                        className="perks-add-people-drawer__button"
                        type="ghost"
                        onClick={() => {
                          setEditSeatModalVisible(true)
                        }}
                      >
                        {translate('admin-dashboard.manage-people.add-people.add-more-button-label')}
                      </Button>
                    </Col>
                  </Row>
                </Col>
              </Row>
            }
            width={window.innerWidth < 768 ? window.innerWidth : 700}
            maskClosable={false}
            onClose={() => {
              setShowAddEmployeeDrawer(false)
            }}
            open={showAddEmployeeDrawer}
            className="cover_zendesk_chat perks-add-people-drawer"
          >
            <AddPeopleForm
              onCancel={() => {
                setShowAddEmployeeDrawer(false)
              }}
              onEmployeeSaved={(addMore) => onEmployeeSaved(addMore)}
            />
          </Drawer>

          {seatsStats && (
            <section>
              <SeatStatistic
                planPeriodId={planPeriod.id}
                loading={seatsStatsLoading}
                toggleEditSeatModal={toggleEditSeatModal}
                {...seatsStats}
              />
            </section>
          )}

          <section>
            <Row className={'mb-24'} justify="space-between">
              <Col xs={4} sm={4} md={2} lg={2} xl={12} className={'mb-8'}>
                {seatsStats && (
                  <>
                    <Button type="primary" icon="fa fa-plus" className={'mr-8'} onClick={onAddPeopleClicked}>
                      {translate('admin-dashboard.manage-people.view-people.button-add-people')}
                    </Button>

                    {/* <Button
                      type="primary"
                      className={'mr-8'}
                      icon="fa fa-layer-plus"
                      onClick={() => history.push(`${ROUTE_PATHS.MANAGE_PEOPLE}/add-bulk-data`)}
                    >
                      Add bulk data
                    </Button> */}

                    {employeesInfo !== null && employeesInfo?.usersToImport.length > 0 && (
                      <Button
                        type="default"
                        className={'primary-color border-color-primary'}
                        icon="far fa-users"
                        onClick={() => history.push(`manage-people/${ROUTE_PATHS.IMPORT_USERS}`)}
                      >
                        Sync {handleGetOtherProductName()} employees
                      </Button>
                    )}
                  </>
                )}
              </Col>
              {/*<Col xs={12} sm={12} md={6} lg={6} xl={6} className={'mb-8'}>*/}
              {/*  {seatsStats && (*/}
              {/*    <Button type="primary" onClick={onSyncPeopleClicked}>*/}
              {/*      {translate('admin-dashboard.manage-people.view-people.button-sync-people-adp')}*/}
              {/*    </Button>*/}
              {/*  )}*/}
              {/*</Col>*/}
              <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                <Row justify="end" gutter={16}>
                  <Col xs={24} sm={24} md={12} lg={12} xl={12} className={'mb-8'}>
                    <Button
                      type={terminatedButton ? 'primary' : 'text'}
                      ghost
                      style={{ backgroundColor: 'white', width: '100%' }}
                      icon={!terminatedButton ? 'fa fa-eye' : 'fa fa-eye-slash'}
                      onClick={onClickTerminatedButton}
                    >
                      {!terminatedButton ? 'Show ' : 'Hide '} Terminated
                    </Button>
                  </Col>
                  <Col xs={24} sm={24} md={12} lg={12} xl={12} className={'mb-8'}>
                    <Input placeholder="Search" suffix={<Icon className="fa fa-search" />} onKeyDown={onSearch} />
                  </Col>
                </Row>
              </Col>
            </Row>
          </section>

          {productTaskDetails && productTaskDetails.status !== TaskStatus.Done && <CompleteAdministratorProfile />}

          {terminatedButton && (
            <section>
              <TerminatedEmployeesTable
                reload={reloadTerminatedEmployeeTable}
                onLoaded={() => {
                  setReloadTerminatedEmployeeTable(false)
                }}
                onRehireClick={() => {
                  setReloadEligibleEmployeeTable(true)
                  setReloadInEligibleEmployeeTable(true)
                }}
                search={search}
              />
            </section>
          )}

          <section>
            <EmployeesTable
              reload={reloadEligibleEmployeeTable}
              onLoaded={() => setReloadEligibleEmployeeTable(false)}
              search={search}
              onTerminateClicked={(user: EmployeeResponse) => {
                setUserToTerminate({
                  employeeId: user.id,
                  fullName: user.firstName,
                  terminationDate: null,
                  planPeriodStartDate: planPeriod.startDate,
                })
              }}
              onOptInOptOutUserClicked={(user: EmployeeResponse, type: string) => {
                if (type === 'optOut') {
                  setUserToOptOut({
                    planPeriodId: planPeriod.id,
                    decision: BenefitParticipationDecision.OptedOut,
                    companyUserId: user.id,
                    effectiveDate: null,
                  })
                  setUserFirstName(user.firstName)
                }
                if (type === 'optIn') {
                  setUserToOptIn({
                    planPeriodId: planPeriod.id,
                    decision: BenefitParticipationDecision.OptedIn,
                    companyUserId: user.id,
                    effectiveDate: null,
                  })
                  setUserToOptInBenefitEligibilityDate(user.benefitEligibilityDate)
                  setUserFirstName(user.firstName)
                }
              }}
              onResendNoticeClicked={async (user: EmployeeResponse) => {
                setLoading(true)
                const result = await sendResendNotice({ companyUserId: user.id, planPeriodId: renewalPlanPeriod.id })
                setLoading(false)

                if (result.completed) {
                  Notification.success('Resent notification successfully.')
                } else {
                  Notification.error('Resent notification failed.')
                }
              }}
              onEditUsernameClicked={(user: EmployeeResponse) => {
                setUserToEditLogin({
                  fullName: user.firstName,
                  username: user.username,
                  employeeId: user.id,
                })
              }}
              planPeriodId={planPeriod.id}
              hasActiveRunOutPeriod={planPeriod.hasActiveRunOutPeriod}
              runOutPeriodYear={planPeriod.runOutPeriodYear}
            />
          </section>

          <section>
            <InEligibleEmployeesTable
              reload={reloadInEligibleEmployeeTable}
              onLoaded={() => setReloadInEligibleEmployeeTable(false)}
              search={search}
              onTerminateClicked={(user: EmployeeResponse) => {
                setUserToTerminate({
                  employeeId: user.id,
                  fullName: user.firstName,
                  terminationDate: null,
                })
              }}
              onOptInOptOutUserClicked={(user: EmployeeResponse, type: string) => {
                if (type === 'optOut') {
                  setUserToOptOut({
                    planPeriodId: planPeriod.id,
                    decision: BenefitParticipationDecision.OptedOut,
                    companyUserId: user.id,
                    effectiveDate: null,
                  })
                  setUserFirstName(user.firstName)
                }
                if (type === 'optIn') {
                  setUserToOptIn({
                    planPeriodId: planPeriod.id,
                    decision: BenefitParticipationDecision.OptedIn,
                    companyUserId: user.id,
                    effectiveDate: null,
                  })
                  setUserToOptInBenefitEligibilityDate(user.benefitEligibilityDate)
                  setUserFirstName(user.firstName)
                }
              }}
            />
          </section>

          {editSeatModalVisible && (
            <EditSeatModal
              visible={editSeatModalVisible}
              onCancel={toggleEditSeatModal}
              onSaved={async () => {
                await reloadStats()
                setEditSeatModalVisible(false)
              }}
              planPeriodId={planPeriod.id}
            />
          )}

          {userToTerminate && (
            <TerminateUserModal
              visible={!!userToTerminate}
              onCancel={() => {
                setUserToTerminate(null)
              }}
              afterSuccessTerminate={() => {
                setUserToTerminate(null)
                setReloadEligibleEmployeeTable(true)
                setReloadInEligibleEmployeeTable(true)
                setReloadTerminatedEmployeeTable(true)
              }}
              userToTerminate={userToTerminate}
            />
          )}
          {userToOptOut && (
            <OptInOptOutModal
              visible={!!userToOptOut}
              onCancel={() => setUserToOptOut(null)}
              danger
              title="Opt employee out of ICHRA"
              description={
                <div>
                  <div>Are you sure?</div>
                  <div>Opting out {userFirstName} will remove their eligibility for the HRA benefit.</div>
                </div>
              }
              action="Opt-out"
              afterSuccessTerminate={handleOptOut}
              isLoading={isMakeDecisionLoading}
              userToOptIn={userToOptOut}
              benefitEligibilityDate={userToOptInBenefitEligibilityDate}
            />
          )}
          {userToOptIn && (
            <OptInOptOutModal
              visible={!!userToOptIn}
              onCancel={() => {
                setUserToOptIn(null)
                setUserToOptInBenefitEligibilityDate(null)
              }}
              title="Opt employee in ICHRA"
              description={
                <div>
                  <div>Are you sure?</div>
                  <div>
                    Opting in {userFirstName} will activate their eligibility for the HRA benefit on the date selected.
                  </div>
                  <div>They will be able to submit expenses incurred during their period of eligibility.</div>
                </div>
              }
              action="Opt-in"
              afterSuccessTerminate={handleOptIn}
              isLoading={isMakeDecisionLoading}
              userToOptIn={userToOptIn}
              benefitEligibilityDate={userToOptInBenefitEligibilityDate}
            />
          )}
          {userToEditLogin && (
            <EditLoginUsernameModal
              visible={!!userToEditLogin}
              onCancel={() => {
                setUserToEditLogin(null)
              }}
              afterSuccessEdit={() => {
                setUserToEditLogin(null)
                setReloadEligibleEmployeeTable(true)
              }}
              userToEdit={userToEditLogin}
            />
          )}
        </div>
      )}
    </Skeleton>
  )
}

export default ViewPeople
